import { useState } from "react";
import { useInView } from "../hooks/useInView";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { EMAIL } from "../data/mock";
import { submitPickup } from "../data/api";
import { useStorePicker } from "../hooks/useStorePicker";
import { Clock, Phone as PhoneIcon, Mail, Send, CheckCircle, Zap, MessageCircle } from "lucide-react";

export const CTASection = () => {
  const [ref, isInView] = useInView();
  const { open: openStorePicker } = useStorePicker();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    address: "",
    service: "laundry",
    timeSlot: "morning",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast.error("Please fill in your name and phone number.");
      return;
    }
    
    try {
      // Submit to API instead of localStorage
      const orderData = {
        name: formData.name,
        phone: formData.phone,
        address: formData.address,
        service: formData.service,
        time_slot: formData.timeSlot
      };
      
      await submitPickup(orderData);
      setSubmitted(true);
      toast.success("Pickup scheduled! We'll confirm via WhatsApp shortly.");
    } catch (error) {
      console.error("Failed to submit pickup:", error);
      toast.error("Failed to schedule pickup. Please try again.");
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (submitted) {
    return (
      <section id="contact" className="py-16 lg:py-20 bg-gradient-to-br from-sky-600 via-sky-700 to-sky-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 border border-white/20 rounded-full" />
          <div className="absolute bottom-10 right-10 w-60 h-60 border border-white/20 rounded-full" />
        </div>
        <div className="relative max-w-2xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <div className="w-20 h-20 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">Pickup Scheduled!</h2>
          <p className="text-lg text-sky-100 leading-relaxed mb-8">
            Thank you, {formData.name}! We'll send a confirmation on WhatsApp shortly. Your fresh clothes are just 24 hours away.
          </p>
          <Button className="bg-white text-sky-700 hover:bg-sky-50 px-8 py-5 rounded-xl font-semibold shadow-xl transition-all duration-300" onClick={() => setSubmitted(false)}>
            Schedule Another Pickup
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-16 lg:py-20 bg-gradient-to-br from-sky-600 via-sky-700 to-sky-800 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-40 h-40 border border-white/20 rounded-full" />
        <div className="absolute bottom-20 right-20 w-60 h-60 border border-white/20 rounded-full" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center" ref={ref}>
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6">
              <Zap className="w-4 h-4 text-lime-400" />
              <span className="text-sm font-semibold text-white">Free Pickup & Delivery</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-5">
              Ready for Fresh,
              <br />Clean Clothes?
            </h2>
            <p className="text-lg text-sky-100 leading-relaxed mb-10">
              Schedule a free pickup and experience Jaipur's fastest laundry service. Your fresh clothes are just 24 hours away.
            </p>
            <div className="space-y-5">
              <button type="button" onClick={() => openStorePicker("call")} className="flex items-center gap-4 group w-full text-left" data-testid="cta-call-btn">
                <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                  <PhoneIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-sky-200">Call Us</p>
                  <p className="text-white font-semibold group-hover:text-lime-300 transition-colors">Pick a store to call</p>
                </div>
              </button>
              <button type="button" onClick={() => openStorePicker("whatsapp")} className="flex items-center gap-4 group w-full text-left" data-testid="cta-whatsapp-btn">
                <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-sky-200">Chat with us</p>
                  <p className="text-white font-semibold group-hover:text-lime-300 transition-colors">Pick a store on WhatsApp</p>
                </div>
              </button>
              <a href={`mailto:${EMAIL}`} className="flex items-center gap-4 group">
                <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-sky-200">Email us</p>
                  <p className="text-white font-semibold group-hover:text-lime-300 transition-colors">{EMAIL}</p>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-sky-200">Working Hours</p>
                  <p className="text-white font-semibold">6 AM – 9 PM, All Days</p>
                </div>
              </div>
            </div>
          </div>

          <div className={`transition-all duration-700 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="bg-white rounded-3xl p-8 shadow-2xl shadow-black/10">
              <h3 className="text-xl font-bold text-slate-900 mb-2">Schedule a Pickup</h3>
              <p className="text-sm text-slate-500 mb-6">Fill in your details and we'll arrange a free pickup.</p>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="pickup-form">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Full Name</label>
                  <Input placeholder="Your full name" value={formData.name} onChange={(e) => handleChange("name", e.target.value)} className="rounded-xl border-slate-200 focus:border-sky-500 focus:ring-sky-500/20 h-12" data-testid="pickup-name-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Phone Number</label>
                  <Input placeholder="+91 XXXXX XXXXX" value={formData.phone} onChange={(e) => handleChange("phone", e.target.value)} className="rounded-xl border-slate-200 focus:border-sky-500 focus:ring-sky-500/20 h-12" data-testid="pickup-phone-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Pickup Address</label>
                  <Input placeholder="Your address in Jaipur" value={formData.address} onChange={(e) => handleChange("address", e.target.value)} className="rounded-xl border-slate-200 focus:border-sky-500 focus:ring-sky-500/20 h-12" data-testid="pickup-address-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Service</label>
                    <select value={formData.service} onChange={(e) => handleChange("service", e.target.value)} className="w-full h-12 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20" data-testid="pickup-service-select">
                      <option value="laundry">Laundry</option>
                      <option value="dry-cleaning">Dry Cleaning</option>
                      <option value="shoe-cleaning">Shoe Cleaning</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Time Slot</label>
                    <select value={formData.timeSlot} onChange={(e) => handleChange("timeSlot", e.target.value)} className="w-full h-12 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20" data-testid="pickup-timeslot-select">
                      <option value="morning">Morning (6-10 AM)</option>
                      <option value="afternoon">Afternoon (12-3 PM)</option>
                      <option value="evening">Evening (5-8 PM)</option>
                    </select>
                  </div>
                </div>
                <Button type="submit" className="w-full bg-sky-600 hover:bg-sky-700 text-white py-6 rounded-xl font-semibold text-base shadow-lg shadow-sky-600/20 hover:shadow-sky-600/30 transition-all duration-300 group mt-2" data-testid="pickup-submit-btn">
                  Schedule Free Pickup
                  <Send className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
